import { createContext, use, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { MenuItem } from '@/consts/navigation';
import { useLocation } from 'react-router';

interface NavigationContextValue {
  expandedSections: Set<string>;
  toggleSection: (sectionText: string) => void;
  isRouteActive: (route: string | undefined) => boolean;
  hasActiveChild: (children: MenuItem[] | undefined) => boolean;
  isNavCollapsed: boolean;
  toggleNavCollapse: () => void;
}

const NavigationContext = createContext<NavigationContextValue | undefined>(undefined);

export const useNavigationContext = () => {
  const context = use(NavigationContext);
  if (!context) {
    throw new Error('useNavigationContext must be used within NavigationProvider');
  }
  return context;
};

interface NavigationProviderProps {
  children: React.ReactNode;
}

export const NavigationProvider = ({ children }: NavigationProviderProps) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => new Set());
  const [isNavCollapsed, setIsNavCollapsed] = useState(false);
  const location = useLocation();

  const activeChildCacheRef = useRef<Map<string, boolean>>(new Map());

  useEffect(() => {
    activeChildCacheRef.current.clear();
  }, [location.pathname]);

  const toggleNavCollapse = useCallback(() => {
    setIsNavCollapsed((prev) => !prev);
  }, []);

  const toggleSection = useCallback((sectionText: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionText)) {
        newSet.delete(sectionText);
      } else {
        newSet.add(sectionText);
      }
      return newSet;
    });
  }, []);

  const pathname = location.pathname;

  const isRouteActive = useCallback(
    (route: string | undefined) => {
      if (!route) return false;
      if (pathname === route) return true;
      return pathname.startsWith(route + '/');
    },
    [pathname]
  );

  const hasActiveChild = useCallback(
    (children: MenuItem[] | undefined) => {
      if (!children || children.length === 0) return false;

      const cacheKey = children.map((c) => c.to ?? '').join('|');
      const cached = activeChildCacheRef.current.get(cacheKey);

      if (cached !== undefined) return cached;

      const checkChildren = (items: MenuItem[]): boolean => {
        for (const child of items) {
          if (isRouteActive(child.to)) return true;
          if (child.children && checkChildren(child.children)) return true;
        }
        return false;
      };

      const result = checkChildren(children);
      activeChildCacheRef.current.set(cacheKey, result);
      return result;
    },
    [isRouteActive]
  );

  const value = useMemo(
    () => ({
      expandedSections,
      toggleSection,
      isRouteActive,
      hasActiveChild,
      isNavCollapsed,
      toggleNavCollapse
    }),
    [expandedSections, toggleSection, isRouteActive, hasActiveChild, isNavCollapsed, toggleNavCollapse]
  );

  return <NavigationContext value={value}>{children}</NavigationContext>;
};
